import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { verifyMfa, resendMfaOtp, getProfile } from '../../api/auth';
import { getBranding } from '../../api/branding';
import { getMyPermissions } from '../../api/rbac';
import { useAuthStore } from '../../stores/authStore';
import { useBrandingStore } from '../../stores/brandingStore';
import { ROUTES } from '../../constants/routes';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30;

export default function MfaVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!(location.state as { fromLogin?: boolean })?.fromLogin) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }
    inputRefs.current[0]?.focus();
  }, [navigate, location]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const handleSubmit = useCallback(async (code: string) => {
    setIsSubmitting(true);
    setError('');

    try {
      const fullToken = await verifyMfa(code);
      useAuthStore.setState({ token: fullToken });

      const [profile, branding] = await Promise.all([getProfile(), getBranding()]);

      useAuthStore.getState().setAuth(profile.user, fullToken, profile.company);
      useBrandingStore.getState().setBranding(branding);

      // Load effective permissions (Layer 1+2+3) — fire-and-forget; non-blocking
      getMyPermissions()
        .then((perms) => useAuthStore.getState().setEffectivePermissions(perms.effective as string[] | '*'))
        .catch(() => {/* silently ignored — CAPABILITY_MAP fallback will be used */});

      if (useAuthStore.getState().isSuperAdmin()) {
        navigate(ROUTES.PLATFORM, { replace: true });
      } else if (profile.user.must_change_password) {
        navigate(ROUTES.FIRST_LOGIN_CHANGE_PASSWORD, { replace: true });
      } else if (useAuthStore.getState().isIcBroker()) {
        navigate(ROUTES.BROKER_DASHBOARD, { replace: true });
      } else if (!profile.user.onboarding_completed) {
        navigate(ROUTES.ONBOARDING, { replace: true });
      } else {
        navigate(ROUTES.DASHBOARD, { replace: true });
      }
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message ?? 'Invalid code. Please try again.';
      setError(message);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setIsSubmitting(false);
    }
  }, [navigate]);

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (next.every((d) => d !== '') && digit) {
      handleSubmit(next.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;

    const next = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      next[i] = pasted[i];
    }
    setDigits(next);

    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();

    if (next.every((d) => d !== '')) {
      handleSubmit(next.join(''));
    }
  };

  const handleResend = async () => {
    try {
      await resendMfaOtp();
      setResendCooldown(RESEND_COOLDOWN);
      setError('');
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message ?? 'Failed to resend code.';
      setError(message);
    }
  };

  return (
    <Card className="w-full max-w-md shadow-lg">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl text-center">Two-factor authentication</CardTitle>
        <CardDescription className="text-center">
          Enter the 6-digit code sent to your email and phone
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-center gap-2">
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={isSubmitting}
              className="h-12 w-10 rounded-md border border-input text-center text-lg font-semibold shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
            />
          ))}
        </div>

        {error && (
          <p className="text-center text-sm font-medium text-destructive">{error}</p>
        )}

        {isSubmitting && (
          <div className="flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isSubmitting}
            className="text-sm text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
          </button>
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(ROUTES.LOGIN)}
          disabled={isSubmitting}
        >
          Back to sign in
        </Button>
      </CardContent>
    </Card>
  );
}
